/**
 * The issue/verify endpoint editor, shared by the add-category and manual-host forms.
 *
 * Both need the same thing: a path, a method, a JSON payload, and (for issue) the dotted
 * key_field - entered as strings and turned into an ApiKeyEndpointConfig, with the JSON
 * validated here so a typo is caught with a field-named message rather than a 422.
 */

import type { ApiKeyEndpointConfig } from '../../api/types'
import { cn } from '../../lib/cn'

/** One endpoint's fields, as the raw strings the inputs hold. */
export interface EndpointFields {
  path: string
  method: string
  payload: string
  keyField: string
}

export const EMPTY_ENDPOINT: EndpointFields = {
  path: '',
  method: 'POST',
  payload: '',
  keyField: '',
}

/** Turn the string fields into config, or throw with a field-named message. */
export function buildEndpoint(
  fields: EndpointFields,
  which: string,
): ApiKeyEndpointConfig | null {
  if (!fields.path.trim()) return null
  let payload: Record<string, unknown> = {}
  if (fields.payload.trim()) {
    try {
      payload = JSON.parse(fields.payload)
    } catch {
      throw new Error(`The ${which} payload is not valid JSON.`)
    }
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
      throw new Error(`The ${which} payload must be a JSON object.`)
    }
  }
  return {
    path: fields.path.trim(),
    method: fields.method.trim() || 'POST',
    payload,
    ...(fields.keyField.trim() ? { key_field: fields.keyField.trim() } : {}),
  }
}

export const inputClass =
  'w-full rounded-md border bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-muted'

export function EndpointFieldset({
  title,
  fields,
  onChange,
  showKeyField,
}: {
  title: string
  fields: EndpointFields
  onChange: (fields: EndpointFields) => void
  showKeyField?: boolean
}) {
  const set = (patch: Partial<EndpointFields>) => onChange({ ...fields, ...patch })
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-semibold text-ink-secondary">{title}</p>
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_5rem]">
        <input
          value={fields.path}
          onChange={(e) => set({ path: e.target.value })}
          placeholder="/api/keys"
          className={inputClass}
        />
        <select
          value={fields.method}
          onChange={(e) => set({ method: e.target.value })}
          className={inputClass}
        >
          {['POST', 'GET', 'PUT'].map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      <textarea
        value={fields.payload}
        onChange={(e) => set({ payload: e.target.value })}
        placeholder='Payload JSON, e.g. {"scope": "health"}'
        rows={2}
        className={cn(inputClass, 'mt-2 font-mono')}
      />
      {showKeyField ? (
        <input
          value={fields.keyField}
          onChange={(e) => set({ keyField: e.target.value })}
          placeholder="key_field, e.g. data.api_key"
          className={cn(inputClass, 'mt-2')}
        />
      ) : null}
    </div>
  )
}
